'use client';

import React, { useEffect, useState, useContext, useRef } from 'react';
import { motion } from 'framer-motion';
import { MediaObject, Slide } from '@/shared/types';
import MediaDisplay, { type MediaDisplayRef } from '../ui/mediaDisplay';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';

// Interfaces and types
interface MediaPaneProps {
  slide: Slide;
  isPresentation?: boolean;
  sourcesExpanded?: boolean;
  hasDescription?: boolean;
  isFullscreen: boolean;
  setFullscreenMediaItem: (mediaItem: MediaObject | null) => void;
  mediaDisplayRef?: React.RefObject<MediaDisplayRef>;
}

// Component definition
const MediaPane: React.FC<MediaPaneProps> = ({
  slide,
  sourcesExpanded,
  hasDescription,
  isFullscreen,
  setFullscreenMediaItem,
  mediaDisplayRef
}) => {
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [currentMedia, setCurrentMedia] = useState<MediaObject | undefined>(slide.media?.[currentMediaIndex || 0] || undefined);
  const [isLoading, setIsLoading] = useState(false);
  const currentSlideRef = useRef<Slide | null>(null);
  useEffect(() => {
    // Reset media index whenever we navigate to a different slide. Using slide object reference
    // (not displayOrder) so we reset even when two slides share the same optional displayOrder.
    if (slide !== currentSlideRef.current) {
      setCurrentMediaIndex(0);
      currentSlideRef.current = slide;
    }
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 10);
  }, [slide]);
  useEffect(() => {
    setCurrentMedia(slide.media?.[currentMediaIndex]);
    // Only sync current media into context while in fullscreen. When not fullscreen, leave
    // fullscreenMediaItem unchanged so mediaDisplay can read the exited item's page number.
    if (isFullscreen && setFullscreenMediaItem) {
      setFullscreenMediaItem(slide.media?.[currentMediaIndex] || null);
    }
  }, [slide.media, currentMediaIndex, isFullscreen]);
  if (!slide.media || slide.media.length === 0 || isLoading) {
    return null;
  }
  return <motion.div className={`mediaPane
        box-border flex max-w-full overflow-x-auto sm:overflow-x-hidden ${sourcesExpanded && !hasDescription ? 'sm:min-h-[400px] min-h-full' : 'sm:min-h-[550px] min-h-full'}
        sm:min-h-0 overflow-y-auto custom-scrollbar2 sm:min-w-[330px] w-full h-full
        ${!slide.mediaSrc && !slide.media?.[0]?.mediaSrc ? 'hidden' : 'slide-boxes lg p-0 m-0 bg-white/5 sm:bg-[#091523]/[.2]'}
        ${isFullscreen ? 'relative top-0 left-0 right-0 bottom-0 z-50 bg-[rgb(15,29,50,0.98)] flex items-center justify-center' : `relative flex ${slide.evidenceSrc || slide.mediaType === 'youtube' ? 'flex-col' : 'flex-col justify-start items-center'}
            ${!slide.slideDescription ? 'w-full' : ''} bg-[rgb(15,29,50,0.5)] sm:p-4`}
        min-h-0
      `} animate={{
    scale: isFullscreen ? 1 : 1
  }} transition={{
    duration: 0.3
  }} data-sentry-element="unknown" data-sentry-component="MediaPane" data-sentry-source-file="mediaPane.tsx">
      <div className="mediaPaneContent flex flex-row items-center w-full box-border p-1 h-[calc(100%-4px)]">
        {!isFullscreen && slide.media.length > 1 && <button className="flex justify-start cursor-pointer disabled:opacity-20 disabled:cursor-not-allowed" onClick={() => setCurrentMediaIndex(currentMediaIndex - 1)} disabled={currentMediaIndex === 0}>
            <CaretLeftIcon size={30} />
          </button>}
        {!!slide.media && slide.media.length > 0 && !!currentMedia && <MediaDisplay ref={mediaDisplayRef ?? undefined} mediaItem={currentMedia} isPresentation={true} sourcesExpanded={sourcesExpanded && !hasDescription} />}
        {!isFullscreen && slide.media.length > 1 && <button className="flex justify-end cursor-pointer disabled:opacity-20 disabled:cursor-not-allowed" onClick={() => setCurrentMediaIndex(currentMediaIndex + 1)} disabled={currentMediaIndex === slide.media.length - 1}>
            <CaretRightIcon size={30} />
          </button>}
      </div>
      {(slide.evidenceSrc || slide.mediaType === 'youtube') && <motion.div className='flex flex-row m-0 p-0 italic font-light text-sm gap-2 text-white/30' animate={{
      opacity: isFullscreen ? 0.5 : 1,
      y: isFullscreen ? 20 : 0
    }}>
        {slide.evidenceSrc && <label className='font-bold'>Source:</label>}
        <span>
          {slide.mediaType === 'youtube' ? slide.mediaSrc || slide.media?.[0]?.mediaSrc : slide.evidenceSrc}
        </span>
      </motion.div>}
    </motion.div>;
};
export default MediaPane;